import { useMemo, useRef } from 'react';
import { cx } from '@/utils';
import { DialogBody, DialogFooter, DialogHeader } from '@material-tailwind/react';

import { useModalDialogStore } from '@/utils/store/store';

import { ButtonWithLogo } from '../ButtonWithLogo/ButtonWithLogo';
import SvgCheckCircle from '../Icons/CheckCircle';
import CrossIcon from '../Icons/CrossIcon';
import SvgExclamationCircle from '../Icons/ExclamationCircle';
import Modal from './Modal';

const SUCCESS = 'success';
const ERROR = 'error';
const CREATE = 'create';

const ModalDialog = () => {
  const {
    modalVisible,
    showVisible,
    title,
    message,
    type,
    onConfirm,
    confirmBtnLabel = 'Confirmar',
    cancelBtnLabel = 'Cancelar',
    showCancelButton = true,
  } = useModalDialogStore();

  const styles = useMemo(() => {
    switch (type) {
      case ERROR:
        return {
          textColor: 'text-redDark',
          btnColor: '!bg-redDark !hover:bg-red-900 border-redDark',
          cancelBtnColor: 'text-redDark !border-redDark hover:bg-redDark',
          icon: <SvgExclamationCircle width={60} height={60} className="text-redDark" />,
        };
      case SUCCESS:
        return {
          textColor: 'text-elfGreen',
          btnColor: '!bg-green !hover:bg-greenDark !border-elfGreen',
          cancelBtnColor: 'text-elfGreen border-elfGreen hover:bg-green',
          icon: <SvgCheckCircle width={60} height={60} className="text-elfGreen" />,
        };

      case CREATE:
        return {
          textColor: 'text-elfGreen',
          btnColor: '!bg-elfGreen !hover:bg-greenDark !hover:text-white !border-elfGreen',
          cancelBtnColor: 'text-elfGreen border-elfGreen hover:bg-elfGreen hover:text-white',
          icon: <CrossIcon width={60} height={60} className="text-elfGreen" />,
        };

      default: {
        return {
          textColor: 'text-marine',
          btnColor: '!bg-marine !hover:bg-blueDark !border-marine',
          cancelBtnColor:
            'text-marine border-marine hover:bg-marine hover:text-white hover:border-marine',
          icon: <SvgExclamationCircle width={60} height={60} className="text-marine" />,
        };
      }
    }
  }, [type]);

  const closeModal = () => showVisible({ modalVisible: false });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const onPressConfirm = () => {
    if (buttonRef.current) buttonRef.current.disabled = true;
    // Close modal first to avoid re-entrancy or re-opening while handler runs
    closeModal();

    try {
      onConfirm();
    } finally {
      delayDisabledBtn();
    }
  };

  const delayDisabledBtn = () =>
    setTimeout(() => {
      if (buttonRef.current) buttonRef.current.disabled = false;
    }, 500);

  return (
    <>
      <Modal
        open={modalVisible}
        handler={showVisible}
        dismiss={{
          escapeKey: false,
          outsidePress: false,
        }}
        className="py-8"
      >
        <DialogHeader className="flex flex-col justify-center gap-[20px] pb-0">
          {styles.icon}
          <label
            className={cx(
              styles.textColor,
              'text-center font-montserrat text-[32px] font-semibold leading-[36px]'
            )}
          >
            {title}
          </label>
        </DialogHeader>
        <DialogBody className="flex flex-col items-center pt-5 text-center font-workSans">
          <label className={'font-workSans text-[16px] font-normal leading-[21px] text-tundora'}>
            {message}
          </label>
        </DialogBody>
        <DialogFooter className="items-center justify-evenly">
          {showCancelButton && (
            <ButtonWithLogo
              onClick={closeModal}
              logo={null}
              nameButton={cancelBtnLabel}
              className={cx(
                'w-[165px] justify-center bg-white font-[500] hover:text-white',
                styles.cancelBtnColor // Aquí ya defines las clases personalizadas
              )}
            />
          )}

          <ButtonWithLogo
            ref={buttonRef}
            logo={null}
            onClick={onPressConfirm}
            nameButton={confirmBtnLabel}
            className={cx('w-[165px] justify-center font-[500] text-white', styles.btnColor)}
          />
        </DialogFooter>
      </Modal>
    </>
  );
};

export default ModalDialog;
